import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ProductGridSkeleton } from "@/components/Skeletons";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SlidersHorizontal, X } from "lucide-react";

const SORT = [
  { v: "newest", l: "NEWEST" },
  { v: "price_asc", l: "PRICE: LOW TO HIGH" },
  { v: "price_desc", l: "PRICE: HIGH TO LOW" },
  { v: "rating", l: "TOP RATED" },
];

const CATEGORIES = ["tops", "bottoms", "footwear", "accessories"];
const SIZES = ["XS","S","M","L","XL","XXL","28","30","32","34","36","7","8","9","10","11","OS"];
const COLORS = ["Black","Bone","Charcoal","White","Red","Olive","Cream","Indigo","Silver","Gold","Stone"];
const FITS = ["regular","oversized","relaxed","tapered","slim","straight","true-to-size","one-size"];

export default function Catalog() {
  const { segment } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q");

  const baseFilters = useMemo(() => {
    const f = {};
    if (segment === "men") f.gender = "men";
    if (segment === "women") f.gender = "women";
    if (segment === "sale") f.tag = "sale";
    if (segment === "collabs") f.tag = "collab";
    if (q) f.q = q;
    return f;
  }, [segment, q]);

  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("newest");
  const [price, setPrice] = useState([0, 8000]);
  const [products, setProducts] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => { setFilters({}); setPrice([0, 8000]); }, [segment, q]);

  useEffect(() => {
    const params = { ...baseFilters, ...filters, sort, min_price: price[0], max_price: price[1] };
    setProducts(null);
    api.get("/products", { params }).then((r) => setProducts(r.data.products));
  }, [baseFilters, filters, sort, price]);

  const title = q ? `RESULTS FOR "${q}"` : (segment || "ALL").toUpperCase();

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v === "all" ? undefined : v }));
  const clearAll = () => { setFilters({}); setPrice([0, 8000]); };

  const activeChips = [
    filters.category && { k: "category", v: filters.category },
    filters.size && { k: "size", v: filters.size },
    filters.color && { k: "color", v: filters.color },
    filters.fit && { k: "fit", v: filters.fit },
    (price[0] !== 0 || price[1] !== 8000) && { k: "price", v: `₹${price[0]}–₹${price[1]}` },
  ].filter(Boolean);

  const filterPanel = (
    <div className="space-y-2">
      <Accordion type="multiple" defaultValue={["category"]} className="space-y-2">
        <AccordionItem value="category" className="border border-black/10">
          <AccordionTrigger className="px-4 py-3 font-display tracking-widest text-xs hover:no-underline">CATEGORY</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <FilterPills value={filters.category} options={CATEGORIES} onChange={(v) => setFilter("category", v)} testIdPrefix="cat" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="size" className="border border-black/10">
          <AccordionTrigger className="px-4 py-3 font-display tracking-widest text-xs hover:no-underline">SIZE</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <FilterPills value={filters.size} options={SIZES} onChange={(v) => setFilter("size", v)} testIdPrefix="size" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="color" className="border border-black/10">
          <AccordionTrigger className="px-4 py-3 font-display tracking-widest text-xs hover:no-underline">COLOR</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <FilterPills value={filters.color} options={COLORS} onChange={(v) => setFilter("color", v)} testIdPrefix="color" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="fit" className="border border-black/10">
          <AccordionTrigger className="px-4 py-3 font-display tracking-widest text-xs hover:no-underline">FIT</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <FilterPills value={filters.fit} options={FITS} onChange={(v) => setFilter("fit", v)} testIdPrefix="fit" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="price" className="border border-black/10">
          <AccordionTrigger className="px-4 py-3 font-display tracking-widest text-xs hover:no-underline">PRICE</AccordionTrigger>
          <AccordionContent className="px-4 pb-5">
            <Slider min={0} max={8000} step={100} value={price} onValueChange={setPrice} />
            <div className="flex justify-between mt-3 text-xs font-mono text-black/60">
              <span>₹{price[0]}</span>
              <span>₹{price[1]}</span>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex gap-2 pt-4">
        <button onClick={clearAll} className="flex-1 border border-black/30 py-3 font-display tracking-widest text-xs hover-invert" data-testid="clear-filters">CLEAR ALL</button>
        <button onClick={() => setFilterOpen(false)} className="flex-1 bg-[#FF3B30] text-white py-3 font-display tracking-widest text-xs hover:bg-black" data-testid="apply-filters">APPLY</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12" data-testid="catalog-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono text-xs text-[#FF3B30] tracking-widest">SHOP / {segment?.toUpperCase() || "ALL"}</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter mt-2">{title}</h1>
          <p className="text-black/50 mt-2 font-mono text-xs">{products ? `${products.length} PRODUCTS` : ""}</p>
        </div>

        <div className="flex items-center gap-3">
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex items-center gap-2 border border-black/30 px-5 py-3 font-display tracking-widest text-xs hover-invert"
                data-testid="open-filters-btn"
              >
                <SlidersHorizontal size={14} /> FILTERS
                {activeChips.length > 0 && (
                  <span className="bg-[#FF3B30] text-white text-[10px] w-5 h-5 flex items-center justify-center">{activeChips.length}</span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-white border-black/10 text-black w-full sm:max-w-md overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-display text-3xl tracking-tighter text-black">FILTERS</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                {filterPanel}
              </div>
            </SheetContent>
          </Sheet>

          <div className="min-w-[200px]">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="bg-transparent border-black/30 rounded-none font-display tracking-widest text-xs" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/30 rounded-none">
                {SORT.map((s) => (
                  <SelectItem key={s.v} value={s.v} className="font-display tracking-widest text-xs">{s.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-black/10">
          <span className="text-xs font-mono text-black/40">ACTIVE:</span>
          {activeChips.map((c) => (
            <button
              key={c.k}
              onClick={() => {
                if (c.k === "price") setPrice([0, 8000]);
                else setFilter(c.k, "all");
              }}
              className="inline-flex items-center gap-1 bg-black text-white px-3 py-1 font-display tracking-widest text-[10px]"
              data-testid={`chip-${c.k}`}
            >
              {c.k.toUpperCase()}: {String(c.v).toUpperCase()} <X size={10} />
            </button>
          ))}
          <button onClick={clearAll} className="text-xs font-mono underline text-black/60 ml-2">Clear all</button>
        </div>
      )}

      {/* Products full width */}
      {products === null ? (
        <ProductGridSkeleton count={12} />
      ) : products.length === 0 ? (
        <div className="py-32 text-center border border-black/10">
          <div className="font-display text-3xl tracking-tighter">NO RESULTS</div>
          <p className="text-black/50 mt-2">Try adjusting filters or search query.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}

function FilterPills({ value, options, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(active ? "all" : opt)}
            data-testid={`filter-${testIdPrefix}-${opt}`}
            className={`px-3 py-1.5 text-[11px] font-display tracking-widest border transition-colors ${
              active ? "bg-black text-white border-black" : "border-black/30 text-black hover:bg-black hover:text-white"
            }`}
          >
            {opt.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
